import { useAtom } from "jotai";
import React, { useEffect, useMemo, useState } from "react";
import {
  StyleSheet,
  Modal,
  Platform,
  useWindowDimensions,
  View,
} from "react-native";
import {
  advertisingAtom,
  advertOverlayModalAtom,
  appHeightAndWidthAtom,
} from "../../atoms";
import {
  AdvertDetails,
  IframeAd,
  InternalAd,
  TakeoverAd,
} from "../../types/advert";
import { advertClick } from "../../client/adverts";
import { logEvent } from "../../client/analytics/trackPageView";
import variables from "../../styles/variables";
import TakeoverAdBanner from "./AdBanners/TakeoverAdBanner";
import RModal from "../RModal/RModal";
import { useResponsive } from "../../hooks/useResponsive";
import AdBanner from "./AdBanners/AdBanner";
import useDeviceType from "../../hooks/useDeviceType";
import { isSafari } from "../Navigation/Utils";

/**
 * AdModal component displays advertisements in a modal format.
 * It checks for takeover (a.k.a gateway ads) or overlay ads and displays them accordingly.
 * If a takeover ad is present, it shows a larger modal; otherwise, it shows a smaller overlay ad.
 */
export const AdModal = () => {
  const windowHeight = useWindowDimensions().height;
  const [showTakeover, setShowTakeover] = useState(true);
  const [advertOverlayModal, setAdvertOverlayModal] = useAtom(
    advertOverlayModalAtom,
  );
  const closeOverlay = () => setAdvertOverlayModal({} as AdvertDetails);
  const [adverts] = useAtom(advertisingAtom);
  const isIframeAd = (advertOverlayModal as IframeAd)?.iframe;

  const { isSmallAndDown, isMediumAndDown } = useResponsive();
  const { deviceType } = useDeviceType();
  const isTablet = deviceType === "TABLET";
  const isDesktop = deviceType === "DESKTOP";

  //Mobile web iframes are very particular in terms of styles and also rerendering issues so they need to be handled separately
  const isMobileWebIframeAd =
    Platform.OS === "web" &&
    (isSmallAndDown || isTablet) &&
    isIframeAd &&
    !isDesktop;

  let width;
  if (Platform.OS != "web") {
    width = useWindowDimensions().width;
  }

  useEffect(() => {
    if (!showTakeover) setShowTakeover(true); // reset takeover ad state when ads change
    if (!adverts) return;
    if (
      !Object.keys(adverts?.takeover || {}).length &&
      !Object.keys(adverts?.overlay_primary || {}).length &&
      !Object.keys(adverts?.overlay_secondary || {}).length
    )
      return;
    // setTimeout for 2 seconds to allow the adverts to load
    // as well as the rest of the app.
    // check and set
    setTimeout(() => {
      setAdvertOverlayModal(
        adverts?.takeover ||
          adverts?.overlay_primary ||
          adverts?.overlay_secondary ||
          {},
      );
    }, 1000);
  }, [adverts]);

  const bannerStyle = useMemo(() => {
    if (isMediumAndDown) {
      return {
        minWidth: isSmallAndDown ? (Platform.OS == "web" ? 350 : "100%") : 500,
        aspectRatio: isIframeAd ? undefined : 16 / 9,
        maxHeight: windowHeight * 0.8,
        top: isIframeAd || Platform.OS == "web" ? 0 : 100,
        // Only do this below on ios or web - ruins android.
        marginLeft: Platform.OS == "ios" || Platform.OS == "web" ? -16 : 0,
      };
    } else {
      return {
        minWidth: 700,
        aspectRatio: isIframeAd ? undefined : 16 / 9,
      };
    }
  }, [isMediumAndDown, isSmallAndDown, isIframeAd, windowHeight]);

  if (!Object.keys(advertOverlayModal || {}).length) return;

  // Show the new takeover if set
  if ((advertOverlayModal as TakeoverAd)?.takeover_id)
    return (
      <TakeoverAdModal
        showTakeover={showTakeover}
        setShowTakeover={setShowTakeover}
        width={width}
        advertOverlayModal={advertOverlayModal}
        closeOverlay={closeOverlay}
      />
    );

  // Otherwise - show the smaller square overlay ad
  return (
    <View
      style={{
        flex:
          !!Object.keys(advertOverlayModal).length && Platform.OS != "web"
            ? 1
            : 0,
      }}
    >
      <RModal
        style={{
          overlay: {
            justifyContent: "center",
            alignItems: "center",
          },

          ...Platform.select({
            web: {
              content: {
                minHeight: 0,
                backgroundColor: "transparent",
                alignSelf: "center",
                justifyContent: "center",
                alignItems: "center",
                maxWidth: isSmallAndDown ? 350 : undefined,
                top:
                  isSmallAndDown && !isMobileWebIframeAd && !isSafari()
                    ? "10%"
                    : 0,
              },
              closeBtn: {
                backgroundColor: "white",
                borderRadius: 20,
                top:
                  isSmallAndDown && !isMobileWebIframeAd && !isSafari()
                    ? "-5%"
                    : 0,
                right: 0,
                padding: 5,
              },
            },
            default: {
              content: {
                backgroundColor: "transparent",
                top:
                  isSmallAndDown || isTablet
                    ? isMobileWebIframeAd
                      ? "0%"
                      : "10%"
                    : "30%",
              },
              closeBtn: {
                backgroundColor: "white",
                borderRadius: 20,
                top:
                  isSmallAndDown || isTablet
                    ? !isIframeAd
                      ? 100
                      : "0%"
                    : "-12%",
                right: isSmallAndDown
                  ? "1%"
                  : isMediumAndDown
                    ? (width - 500) / 2 - 12
                    : (width - 700) / 2 - 12,
                padding: isSmallAndDown ? 5 : 10,
              },
            },
          }),
        }}
        visible={!!Object.keys(advertOverlayModal).length}
        setVisibility={async () => closeOverlay()}
        adOverlay
      >
        {!isIframeAd ? (
          <View
            style={{
              display: "flex",
              marginLeft: 16,
            }}
            fsClass="fs-unmask"
          >
            <AdBanner
              onPress={() => {
                logEvent({ key: "overlay-advert-clicked", data: {} });
                advertClick((advertOverlayModal as InternalAd).link_url);
                closeOverlay();
              }}
              // setting any styles here other than null (even an empty object) causes the iframe ad to rerender on mobile web, the styles aren't needed in these cases so they're set as null
              style={isMobileWebIframeAd ? null : bannerStyle}
              ad={advertOverlayModal}
            />
          </View>
        ) : (
          <AdBanner
            onPress={() => {
              logEvent({ key: "overlay-advert-clicked", data: {} });
              advertClick((advertOverlayModal as IframeAd).link_url);
              closeOverlay();
            }}
            // setting any styles here other than null (even an empty object) causes the iframe ad to rerender on mobile web, the styles aren't needed in these cases so they're set as null
            style={isMobileWebIframeAd ? null : bannerStyle}
            ad={advertOverlayModal}
          />
        )}
      </RModal>
    </View>
  );
};

const TakeoverAdModal = ({
  showTakeover,
  setShowTakeover,
  width,
  advertOverlayModal,
  closeOverlay,
}) => {
  const handleClose = () => {
    setShowTakeover(false);
    closeOverlay();
  };

  const TakeoverAd = () => (
    <TakeoverAdBanner closeFunction={handleClose} ad={advertOverlayModal} />
  );

  return (
    <Modal transparent visible={showTakeover}>
      {width >= variables.width4K && Platform.OS === "web" ? (
        <div className="exclude-zoom" style={styles.fourK}>
          <TakeoverAd />
        </div>
      ) : (
        <TakeoverAd />
      )}
    </Modal>
  );
};

const styles = StyleSheet.create({
  fourK: {
    marginTop: "auto",
    marginBottom: "auto",
    display: "flex",
  },
});
